import { useState } from "react";
import { useIntl } from "react-intl";

import { ReactComponent as Triangle } from "assets/images/iconsV2/triangle-alert.svg";
import Button from "../components/v2/Button/Button";
import Modal from "../components/v2/Modal/Modal";

const useConfirm = () => {
  const intl = useIntl();
  const defaultConfirmText = intl.formatMessage({ id: "general.confirm", defaultMessage: "Confirm" });
  const defaultCancelText = intl.formatMessage({ id: "general.cancel", defaultMessage: "Cancel" });

  const [modalOpen, setModalOpen] = useState(false);
  const [resolvePromise, setResolvePromise] = useState<(value: boolean) => void>();
  const [modalConfig, setModalConfig] = useState<{
    title: string;
    message: string;
    confirmBtnText: string;
    cancelBtnText: string;
    icon: JSX.Element;
    disableCancel?: boolean;
  }>({
    title: "",
    message: "",
    confirmBtnText: defaultConfirmText,
    cancelBtnText: defaultCancelText,
    icon: <Triangle />,
    disableCancel: false,
  });

  const confirm = ({
    title,
    message,
    confirmBtnText = defaultConfirmText,
    cancelBtnText = defaultCancelText,
    icon = <Triangle />,
    disableCancel = false,
  }: {
    title: string;
    message: string;
    confirmBtnText?: string;
    cancelBtnText?: string;
    icon?: JSX.Element;
    disableCancel?: boolean;
  }): Promise<boolean> => {
    setModalConfig({
      title,
      message,
      confirmBtnText,
      cancelBtnText,
      icon,
      disableCancel,
    });
    setModalOpen(true);

    return new Promise((resolve) => {
      setResolvePromise(() => resolve);
    });
  };

  const handleConfirm = () => {
    setModalOpen(false);
    resolvePromise?.(true);
  };

  const handleCancel = () => {
    setModalOpen(false);
    resolvePromise?.(false);
  };

  function ConfirmModal() {
    return (
      <Modal show={modalOpen} onHide={handleCancel} size="md">
        <Modal.Header>
          <div className="d-flex flex-column">
            <div
              style={{ width: 48, height: 48 }}
              className="d-flex justify-content-center align-items-center p-lg shadow-xs radius-full bg-warning-secondary"
            >
              <Triangle />
            </div>
            <div className="mt-xl text-primary text-xl font-semibold">{modalConfig.title}</div>
            <div className="mt-xl text-tertiary text-sm">{modalConfig.message}</div>
          </div>
        </Modal.Header>
        <Modal.Footer>
          <div className="d-flex align-items-center justify-content-end">
            {!modalConfig.disableCancel && (
              <div className="mr-lg">
                <Button label={modalConfig.cancelBtnText} onClick={handleCancel} variant="secondary-gray" />
              </div>
            )}
            <Button label={modalConfig.confirmBtnText} onClick={handleConfirm} />
          </div>
        </Modal.Footer>
      </Modal>
    );
  }

  return { confirm, ConfirmModal };
};

export default useConfirm;
